import React, { useState, useEffect } from "react";
import {
    FaTicketAlt,
    FaFilter,
    FaDownload,
    FaEye,
    FaEdit,
    FaTrash,
    FaUserCheck,
    FaClock,
    FaExclamationCircle,
    FaCheckCircle,
    FaArrowUp,
    FaSearch,
    FaTimes,
    FaUserPlus,
    FaBars,
    FaEllipsisV
} from "react-icons/fa";

const SuperAdminTicketManagement = () => {
    const [tickets, setTickets] = useState([
        {
            id: "TICK-789456",
            customer: "John Doe",
            email: "john@example.com",
            phone: "+1234567890",
            issue: "Order not delivered after 2 hours",
            category: "Delivery",
            priority: "High",
            status: "Open",
            agent: "Agent Smith",
            created: "2024-01-15 14:30",
            lastUpdate: "2024-01-15 15:15",
            responseTime: "45m",
            customerRating: 4.5,
            tags: ["late", "refund", "urgent"],
            source: "Mobile App",
            sla: "30m"
        },
        {
            id: "TICK-123789",
            customer: "Jane Smith",
            email: "jane@example.com",
            phone: "+0987654321",
            issue: "Payment failed but amount deducted",
            category: "Payment",
            priority: "Critical",
            status: "In Progress",
            agent: "Agent Johnson",
            created: "2024-01-15 10:15",
            lastUpdate: "2024-01-15 14:45",
            responseTime: "15m",
            customerRating: null,
            tags: ["payment", "technical", "refund"],
            source: "Website",
            sla: "15m"
        }
    ]);

    const [filters, setFilters] = useState({
        status: "all",
        priority: "all",
        agent: "all",
        category: "all",
        dateRange: "today",
        search: ""
    });

    const [showFilters, setShowFilters] = useState(false);
    const [selectedTickets, setSelectedTickets] = useState([]);
    const [showTicketModal, setShowTicketModal] = useState(false);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [isMobile, setIsMobile] = useState(false);
    const [showMobileMenu, setShowMobileMenu] = useState(false);

    // Check if mobile
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const agents = [
        { id: 1, name: "Agent Smith", status: "online" },
        { id: 2, name: "Agent Johnson", status: "online" },
        { id: 3, name: "Agent Williams", status: "away" },
        { id: 4, name: "Agent Davis", status: "offline" }
    ];

    const priorities = [
        { value: "critical", label: "Critical", color: "#dc3545" },
        { value: "high", label: "High", color: "#fd7e14" },
        { value: "medium", label: "Medium", color: "#ffc107" },
        { value: "low", label: "Low", color: "#28a745" }
    ];

    const statuses = [
        { value: "open", label: "Open", color: "#dc3545" },
        { value: "in-progress", label: "In Progress", color: "#007bff" },
        { value: "resolved", label: "Resolved", color: "#28a745" },
        { value: "escalated", label: "Escalated", color: "#6f42c1" },
        { value: "closed", label: "Closed", color: "#6c757d" }
    ];

    const handleFilterChange = (filterName, value) => {
        setFilters({ ...filters, [filterName]: value });
    };

    const handleSelectTicket = (ticketId) => {
        if (selectedTickets.includes(ticketId)) {
            setSelectedTickets(selectedTickets.filter(id => id !== ticketId));
        } else {
            setSelectedTickets([...selectedTickets, ticketId]);
        }
    };

    const handleBulkAction = (action) => {
        switch (action) {
            case 'assign':
                const agentName = prompt("Enter agent name to assign tickets to:");
                if (agentName) {
                    setTickets(tickets.map(ticket =>
                        selectedTickets.includes(ticket.id) ? { ...ticket, agent: agentName } : ticket
                    ));
                    setSelectedTickets([]);
                }
                break;
            case 'close':
                if (window.confirm(`Close ${selectedTickets.length} tickets?`)) {
                    setTickets(tickets.map(ticket =>
                        selectedTickets.includes(ticket.id) ? { ...ticket, status: "Closed" } : ticket
                    ));
                    setSelectedTickets([]);
                }
                break;
            case 'delete':
                if (window.confirm(`Delete ${selectedTickets.length} tickets?`)) {
                    setTickets(tickets.filter(ticket => !selectedTickets.includes(ticket.id)));
                    setSelectedTickets([]);
                }
                break;
        }
    };

    const viewTicketDetails = (ticket) => {
        setSelectedTicket(ticket);
        setShowTicketModal(true);
    };

    const getStatusColor = (status) => {
        switch (status.toLowerCase()) {
            case 'open': return '#dc3545';
            case 'in progress': return '#007bff';
            case 'resolved': return '#28a745';
            case 'escalated': return '#6f42c1';
            case 'closed': return '#6c757d';
            default: return '#6c757d';
        }
    };

    const getPriorityColor = (priority) => {
        switch (priority.toLowerCase()) {
            case 'critical': return '#dc3545';
            case 'high': return '#fd7e14';
            case 'medium': return '#ffc107';
            case 'low': return '#28a745';
            default: return '#6c757d';
        }
    };

    // Mobile responsive styles
    const containerStyle = {
        padding: isMobile ? "10px" : "20px",
        fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
        maxWidth: "100%",
        overflowX: "hidden"
    };

    const headerStyle = {
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        justifyContent: "space-between",
        alignItems: isMobile ? "flex-start" : "center",
        marginBottom: isMobile ? "20px" : "30px",
        gap: isMobile ? "15px" : "20px",
        flexWrap: "wrap"
    };

    const titleStyle = {
        fontSize: isMobile ? "22px" : "28px",
        fontWeight: "700",
        color: "#2c3e50",
        margin: "0",
        display: "flex",
        alignItems: "center",
        gap: "8px"
    };

    const searchContainerStyle = {
        position: "relative",
        width: isMobile ? "100%" : "300px"
    };

    const actionsContainerStyle = {
        display: "flex",
        gap: isMobile ? "10px" : "15px",
        alignItems: "center",
        flexWrap: "wrap",
        width: isMobile ? "100%" : "auto"
    };

    const buttonStyle = (color) => ({
        padding: isMobile ? "8px 12px" : "10px 20px",
        backgroundColor: color,
        color: "white",
        border: "none",
        borderRadius: "5px",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: "6px",
        fontSize: isMobile ? "12px" : "14px",
        fontWeight: "500",
        whiteSpace: "nowrap"
    });

    const tableContainerStyle = {
        backgroundColor: "#fff",
        borderRadius: "10px",
        overflow: "hidden",
        boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
        overflowX: "auto",
        WebkitOverflowScrolling: "touch"
    };

    const tableStyle = {
        width: "100%",
        borderCollapse: "collapse",
        minWidth: isMobile ? "800px" : "100%"
    };

    const thStyle = {
        padding: isMobile ? "12px 8px" : "15px",
        textAlign: "left",
        fontWeight: "600",
        color: "#495057",
        backgroundColor: "#f8f9fa",
        borderBottom: "2px solid #e9ecef",
        fontSize: isMobile ? "12px" : "14px",
        whiteSpace: "nowrap"
    };

    const tdStyle = {
        padding: isMobile ? "12px 8px" : "15px",
        borderBottom: "1px solid #e9ecef",
        verticalAlign: "middle",
        fontSize: isMobile ? "12px" : "14px"
    };

    const actionButtonStyle = (color) => ({
        padding: isMobile ? "4px 8px" : "6px 12px",
        backgroundColor: color,
        color: "white",
        border: "none",
        borderRadius: "4px",
        cursor: "pointer",
        fontSize: isMobile ? "11px" : "12px",
        marginRight: "4px",
        marginBottom: isMobile ? "4px" : "0",
        display: "inline-flex",
        alignItems: "center",
        gap: "4px"
    });

    const mobileCardStyle = {
        backgroundColor: "#fff",
        borderRadius: "10px",
        padding: "15px",
        marginBottom: "15px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        display: "none" // Hidden by default, shown only on mobile
    };

    return (
        <div style={containerStyle}>
            {/* Mobile Menu Toggle */}
            {isMobile && (
                <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "15px",
                    paddingBottom: "10px",
                    borderBottom: "1px solid #e9ecef"
                }}>
                    <button
                        style={{
                            padding: "8px",
                            backgroundColor: "transparent",
                            border: "1px solid #ddd",
                            borderRadius: "5px",
                            cursor: "pointer"
                        }}
                        onClick={() => setShowMobileMenu(!showMobileMenu)}
                    >
                        <FaBars />
                    </button>
                    <span style={{ fontSize: "16px", fontWeight: "600", color: "#2c3e50" }}>
                        Ticket Management
                    </span>
                    <button
                        style={{
                            padding: "8px",
                            backgroundColor: "transparent",
                            border: "none",
                            cursor: "pointer"
                        }}
                        onClick={() => setShowFilters(!showFilters)}
                    >
                        <FaFilter />
                    </button>
                </div>
            )}

            {/* Header - Desktop */}
            {!isMobile && (
                <div style={headerStyle}>
                    <h1 style={titleStyle}>
                        <FaTicketAlt /> Ticket Management
                        <span style={{ fontSize: "14px", color: "#6c757d", fontWeight: "normal", marginLeft: "10px" }}>
                            {tickets.length} tickets found
                        </span>
                    </h1>
                    <div style={actionsContainerStyle}>
                        <div style={searchContainerStyle}>
                            <input
                                type="text"
                                placeholder="Search tickets..."
                                style={{
                                    width: "100%",
                                    padding: "10px 40px 10px 15px",
                                    border: "1px solid #ddd",
                                    borderRadius: "5px",
                                    fontSize: "14px",
                                    outline: "none"
                                }}
                                value={filters.search}
                                onChange={(e) => handleFilterChange("search", e.target.value)}
                            />
                            <div style={{
                                position: "absolute",
                                right: "15px",
                                top: "50%",
                                transform: "translateY(-50%)",
                                color: "#6c757d"
                            }}>
                                <FaSearch />
                            </div>
                        </div>
                        <button
                            style={buttonStyle("#6a0dad")}
                            onClick={() => setShowFilters(!showFilters)}
                        >
                            <FaFilter /> {showFilters ? "Hide Filters" : "Show Filters"}
                        </button>
                        <button style={buttonStyle("#28a745")}>
                            <FaDownload /> Export
                        </button>
                    </div>
                </div>
            )}

            {/* Mobile Search - Only shown on mobile */}
            {isMobile && (
                <div style={{
                    marginBottom: "15px",
                    position: "relative",
                    width: "100%"
                }}>
                    <input
                        type="text"
                        placeholder="Search tickets..."
                        style={{
                            width: "100%",
                            padding: "12px 40px 12px 15px",
                            border: "1px solid #ddd",
                            borderRadius: "5px",
                            fontSize: "14px",
                            outline: "none"
                        }}
                        value={filters.search}
                        onChange={(e) => handleFilterChange("search", e.target.value)}
                    />
                    <div style={{
                        position: "absolute",
                        right: "15px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        color: "#6c757d"
                    }}>
                        <FaSearch />
                    </div>
                </div>
            )}

            {/* Filter Panel */}
            {showFilters && (
                <div style={{
                    backgroundColor: "#fff",
                    borderRadius: "10px",
                    padding: isMobile ? "15px" : "20px",
                    marginBottom: "20px",
                    boxShadow: "0 2px 10px rgba(0,0,0,0.1)"
                }}>
                    <div style={{
                        display: "grid",
                        gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit, minmax(200px, 1fr))",
                        gap: isMobile ? "15px" : "20px",
                        marginBottom: "20px"
                    }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                            <label style={{ fontSize: "14px", fontWeight: "500", color: "#495057" }}>
                                Status
                            </label>
                            <select
                                style={{
                                    padding: "10px",
                                    border: "1px solid #ddd",
                                    borderRadius: "5px",
                                    fontSize: "14px",
                                    backgroundColor: "#fff",
                                    cursor: "pointer",
                                    width: "100%"
                                }}
                                value={filters.status}
                                onChange={(e) => handleFilterChange("status", e.target.value)}
                            >
                                <option value="all">All Status</option>
                                {statuses.map((status, index) => (
                                    <option key={index} value={status.value}>{status.label}</option>
                                ))}
                            </select>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                            <label style={{ fontSize: "14px", fontWeight: "500", color: "#495057" }}>
                                Priority
                            </label>
                            <select
                                style={{
                                    padding: "10px",
                                    border: "1px solid #ddd",
                                    borderRadius: "5px",
                                    fontSize: "14px",
                                    backgroundColor: "#fff",
                                    cursor: "pointer",
                                    width: "100%"
                                }}
                                value={filters.priority}
                                onChange={(e) => handleFilterChange("priority", e.target.value)}
                            >
                                <option value="all">All Priorities</option>
                                {priorities.map((priority, index) => (
                                    <option key={index} value={priority.value}>{priority.label}</option>
                                ))}
                            </select>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                            <label style={{ fontSize: "14px", fontWeight: "500", color: "#495057" }}>
                                Agent
                            </label>
                            <select
                                style={{
                                    padding: "10px",
                                    border: "1px solid #ddd",
                                    borderRadius: "5px",
                                    fontSize: "14px",
                                    backgroundColor: "#fff",
                                    cursor: "pointer",
                                    width: "100%"
                                }}
                                value={filters.agent}
                                onChange={(e) => handleFilterChange("agent", e.target.value)}
                            >
                                <option value="all">All Agents</option>
                                <option value="unassigned">Unassigned</option>
                                {agents.map((agent, index) => (
                                    <option key={index} value={agent.name}>{agent.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>
            )}

            {/* Bulk Actions */}
            {selectedTickets.length > 0 && (
                <div style={{
                    backgroundColor: "#f8f9fa",
                    padding: isMobile ? "12px" : "15px 20px",
                    borderRadius: "8px",
                    marginBottom: "20px",
                    display: "flex",
                    flexDirection: isMobile ? "column" : "row",
                    alignItems: isMobile ? "stretch" : "center",
                    gap: isMobile ? "10px" : "15px",
                    flexWrap: "wrap"
                }}>
                    <span style={{ fontWeight: "600", color: "#2c3e50", fontSize: isMobile ? "14px" : "16px" }}>
                        {selectedTickets.length} tickets selected
                    </span>
                    <div style={{
                        display: "flex",
                        gap: isMobile ? "8px" : "15px",
                        flexWrap: "wrap"
                    }}>
                        <button
                            style={buttonStyle("#6a0dad")}
                            onClick={() => handleBulkAction('assign')}
                        >
                            <FaUserPlus /> Assign
                        </button>
                        <button
                            style={buttonStyle("#28a745")}
                            onClick={() => handleBulkAction('close')}
                        >
                            <FaCheckCircle /> Close
                        </button>
                        <button
                            style={buttonStyle("#dc3545")}
                            onClick={() => handleBulkAction('delete')}
                        >
                            <FaTrash /> Delete
                        </button>
                    </div>
                </div>
            )}

            {/* Mobile Cards View - Only on mobile */}
            {isMobile && (
                <div style={{ marginBottom: "20px" }}>
                    {tickets.map((ticket) => (
                        <div key={ticket.id} style={mobileCardStyle}>
                            <div style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: "10px"
                            }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                    <input
                                        type="checkbox"
                                        checked={selectedTickets.includes(ticket.id)}
                                        onChange={() => handleSelectTicket(ticket.id)}
                                        style={{ width: "16px", height: "16px" }}
                                    />
                                    <div style={{ fontWeight: "600", color: "#667eea", fontSize: "14px" }}>
                                        {ticket.id}
                                    </div>
                                </div>
                                <span style={{
                                    padding: "4px 8px",
                                    borderRadius: "4px",
                                    fontSize: "10px",
                                    fontWeight: "600",
                                    backgroundColor: getStatusColor(ticket.status) + "20",
                                    color: getStatusColor(ticket.status)
                                }}>
                                    {ticket.status}
                                </span>
                            </div>
                            
                            <div style={{ marginBottom: "8px" }}>
                                <div style={{ fontWeight: "500", fontSize: "14px" }}>{ticket.customer}</div>
                                <div style={{ fontSize: "12px", color: "#6c757d", marginBottom: "4px" }}>{ticket.email}</div>
                            </div>
                            
                            <div style={{ marginBottom: "8px" }}>
                                <div style={{ fontSize: "13px", color: "#495057", marginBottom: "4px" }}>
                                    {ticket.issue.length > 60 ? ticket.issue.substring(0, 60) + "..." : ticket.issue}
                                </div>
                                <div style={{ fontSize: "11px", color: "#6c757d" }}>
                                    {ticket.category}
                                </div>
                            </div>
                            
                            <div style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: "10px"
                            }}>
                                <span style={{
                                    padding: "3px 8px",
                                    borderRadius: "4px",
                                    fontSize: "10px",
                                    fontWeight: "600",
                                    backgroundColor: getPriorityColor(ticket.priority) + "20",
                                    color: getPriorityColor(ticket.priority)
                                }}>
                                    {ticket.priority}
                                </span>
                                <div style={{ fontSize: "11px", color: "#6c757d" }}>
                                    {ticket.created}
                                </div>
                            </div>
                            
                            <div style={{
                                display: "flex",
                                gap: "8px",
                                justifyContent: "center"
                            }}>
                                <button
                                    style={actionButtonStyle("#6a0dad")}
                                    onClick={() => viewTicketDetails(ticket)}
                                >
                                    <FaEye /> View
                                </button>
                                <button style={actionButtonStyle("#ffc107")}>
                                    <FaEdit />
                                </button>
                                <button style={actionButtonStyle("#dc3545")}>
                                    <FaTrash />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Tickets Table - Desktop */}
            {!isMobile && (
                <div style={tableContainerStyle}>
                    <div style={{ overflowX: "auto" }}>
                        <table style={tableStyle}>
                            <thead>
                                <tr>
                                    <th style={thStyle}>
                                        <input type="checkbox" style={{ width: "16px", height: "16px" }} />
                                    </th>
                                    <th style={thStyle}>Ticket ID</th>
                                    <th style={thStyle}>Customer</th>
                                    <th style={thStyle}>Issue</th>
                                    <th style={thStyle}>Priority</th>
                                    <th style={thStyle}>Status</th>
                                    <th style={thStyle}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tickets.map((ticket) => (
                                    <tr key={ticket.id}>
                                        <td style={tdStyle}>
                                            <input
                                                type="checkbox"
                                                checked={selectedTickets.includes(ticket.id)}
                                                onChange={() => handleSelectTicket(ticket.id)}
                                                style={{ width: "16px", height: "16px" }}
                                            />
                                        </td>
                                        <td style={tdStyle}>
                                            <div style={{ fontWeight: "600", color: "#667eea" }}>{ticket.id}</div>
                                            <div style={{ fontSize: "12px", color: "#6c757d" }}>{ticket.created}</div>
                                        </td>
                                        <td style={tdStyle}>
                                            <div style={{ fontWeight: "500" }}>{ticket.customer}</div>
                                            <div style={{ fontSize: "12px", color: "#6c757d" }}>{ticket.email}</div>
                                        </td>
                                        <td style={tdStyle}>
                                            <div style={{ fontWeight: "500", marginBottom: "5px" }}>{ticket.issue}</div>
                                            <div style={{ fontSize: "12px", color: "#6c757d" }}>
                                                {ticket.category}
                                            </div>
                                        </td>
                                        <td style={tdStyle}>
                                            <span style={{
                                                padding: "4px 10px",
                                                borderRadius: "4px",
                                                fontSize: "12px",
                                                fontWeight: "600",
                                                display: "inline-block",
                                                backgroundColor: getPriorityColor(ticket.priority) + "20",
                                                color: getPriorityColor(ticket.priority)
                                            }}>
                                                {ticket.priority}
                                            </span>
                                        </td>
                                        <td style={tdStyle}>
                                            <span style={{
                                                padding: "5px 12px",
                                                borderRadius: "20px",
                                                fontSize: "12px",
                                                fontWeight: "600",
                                                display: "inline-block",
                                                backgroundColor: getStatusColor(ticket.status) + "20",
                                                color: getStatusColor(ticket.status)
                                            }}>
                                                {ticket.status}
                                            </span>
                                        </td>
                                        <td style={tdStyle}>
                                            <button
                                                style={actionButtonStyle("#6a0dad")}
                                                onClick={() => viewTicketDetails(ticket)}
                                            >
                                                <FaEye /> View
                                            </button>
                                            <button style={actionButtonStyle("#ffc107")}>
                                                <FaEdit />
                                            </button>
                                            <button style={actionButtonStyle("#dc3545")}>
                                                <FaTrash />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Ticket Details Modal - Responsive */}
            {showTicketModal && selectedTicket && (
                <div style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "rgba(0,0,0,0.5)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 1000,
                    padding: isMobile ? "10px" : "20px"
                }} onClick={() => setShowTicketModal(false)}>
                    <div style={{
                        backgroundColor: "#fff",
                        borderRadius: "10px",
                        padding: isMobile ? "15px" : "30px",
                        maxWidth: isMobile ? "95%" : "800px",
                        width: "100%",
                        maxHeight: isMobile ? "85vh" : "90vh",
                        overflowY: "auto",
                        position: "relative"
                    }} onClick={(e) => e.stopPropagation()}>
                        <div style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: isMobile ? "15px" : "20px"
                        }}>
                            <h2 style={{
                                fontSize: isMobile ? "18px" : "24px",
                                fontWeight: "600",
                                color: "#2c3e50",
                                margin: "0"
                            }}>
                                Ticket: {selectedTicket.id}
                            </h2>
                            <button style={{
                                backgroundColor: "transparent",
                                border: "none",
                                fontSize: isMobile ? "20px" : "24px",
                                color: "#6c757d",
                                cursor: "pointer",
                                padding: "0"
                            }} onClick={() => setShowTicketModal(false)}>
                                <FaTimes />
                            </button>
                        </div>

                        <div style={{
                            display: "grid",
                            gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit, minmax(200px, 1fr))",
                            gap: isMobile ? "15px" : "20px",
                            marginBottom: isMobile ? "20px" : "30px"
                        }}>
                            <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                                <div style={{ fontSize: "12px", color: "#6c757d", fontWeight: "500", textTransform: "uppercase" }}>
                                    Customer
                                </div>
                                <div style={{ fontSize: isMobile ? "14px" : "16px", color: "#2c3e50", fontWeight: "500" }}>
                                    {selectedTicket.customer}
                                </div>
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                                <div style={{ fontSize: "12px", color: "#6c757d", fontWeight: "500", textTransform: "uppercase" }}>
                                    Email
                                </div>
                                <div style={{ fontSize: isMobile ? "14px" : "16px", color: "#2c3e50", fontWeight: "500" }}>
                                    {selectedTicket.email}
                                </div>
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                                <div style={{ fontSize: "12px", color: "#6c757d", fontWeight: "500", textTransform: "uppercase" }}>
                                    Status
                                </div>
                                <div style={{ fontSize: isMobile ? "14px" : "16px", color: "#2c3e50", fontWeight: "500" }}>
                                    <span style={{
                                        padding: "4px 12px",
                                        borderRadius: "20px",
                                        fontSize: "12px",
                                        fontWeight: "600",
                                        backgroundColor: getStatusColor(selectedTicket.status) + "20",
                                        color: getStatusColor(selectedTicket.status)
                                    }}>
                                        {selectedTicket.status}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div style={{ marginBottom: isMobile ? "15px" : "20px" }}>
                            <div style={{ fontSize: "12px", color: "#6c757d", fontWeight: "500", textTransform: "uppercase", marginBottom: "5px" }}>
                                Issue Description
                            </div>
                            <div style={{
                                padding: isMobile ? "12px" : "15px",
                                backgroundColor: "#f8f9fa",
                                borderRadius: "8px",
                                color: "#2c3e50",
                                fontSize: isMobile ? "14px" : "16px",
                                lineHeight: "1.5"
                            }}>
                                {selectedTicket.issue}
                            </div>
                        </div>

                        <div style={{
                            display: "flex",
                            flexDirection: isMobile ? "column" : "row",
                            gap: isMobile ? "10px" : "10px",
                            justifyContent: "flex-end"
                        }}>
                            <button style={{
                                padding: isMobile ? "10px" : "10px 20px",
                                backgroundColor: "#6c757d",
                                color: "white",
                                border: "none",
                                borderRadius: "5px",
                                cursor: "pointer",
                                fontSize: isMobile ? "13px" : "14px",
                                fontWeight: "500",
                                width: isMobile ? "100%" : "auto"
                            }} onClick={() => setShowTicketModal(false)}>
                                Close
                            </button>
                            <button style={{
                                padding: isMobile ? "10px" : "10px 20px",
                                backgroundColor: "#6a0dad",
                                color: "white",
                                border: "none",
                                borderRadius: "5px",
                                cursor: "pointer",
                                fontSize: isMobile ? "13px" : "14px",
                                fontWeight: "500",
                                width: isMobile ? "100%" : "auto"
                            }}>
                                Send Response
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SuperAdminTicketManagement;